import { useEffect, useState } from "react";
import { api, type SettingsResponse } from "../api/client";
import ParamEditForm from "../components/settings/ParamEditForm";
import ParameterVersions from "../components/settings/ParameterVersions";
import EffectiveRail from "../components/settings/EffectiveRail";
import MarketCalendarPanel from "../components/settings/MarketCalendarPanel";
import SettingsStrip from "../components/settings/SettingsStrip";
import AppShell from "../components/layout/AppShell";
import PageOwner from "../components/common/PageOwner";
import Panel from "../components/common/Panel";
import { ErrorState, LoadingState } from "../components/common/states";

/**
 * SPEC §14 Settings page — V20 layout: one summary strip (modes · active version ·
 * drift counter · build) + two columns. Work column (left): the unified
 * "Parameters" panel (edit form → candidate appears in the versions list right
 * below, with per-row Activate and inline rollback — the whole loop in one panel)
 * and the Market calendar panel (weekly schedule + IG-seeded exceptions + the
 * pre-close flatten facts). Reference rail (right, sticky): every effective value
 * the engine runs, grouped and dense, with amber "≠ default" drift markers.
 *
 * Render-state contract (B5b): loading → spinner; error → explicit block (no stale
 * data); data → strip + columns. DEMO and LIVE are separate dedicated deployments
 * (SPEC §19) — one deployment shows its own value set.
 */
export default function SettingsPage() {
  const [data, setData] = useState<SettingsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Bumped after each created candidate: remounts ParameterVersions so it refetches.
  // Without this the versions list never reloaded (it fetches on mount only, and the
  // page keeps `data` non-null during reload() so the subtree is never unmounted) —
  // a freshly saved candidate stayed invisible until a full page refresh (2026-07-15:
  // owner clicked "Create candidate" 4× and got 4 identical candidates, ids 43-46).
  const [versionsKey, setVersionsKey] = useState(0);

  const reload = () => {
    setLoading(true);
    setError(null);
    api
      .settings()
      .then((s) => {
        setData(s);
        setLoading(false);
      })
      .catch(() => {
        setData(null);
        setError("Échec du chargement de /api/settings");
        setLoading(false);
      });
  };

  useEffect(() => {
    reload();
  }, []);

  return (
    <AppShell>
      <div className="stagger flex flex-col gap-4">
        {/* Variante B (2026-07-31) : paramètres du moteur — Commandes n'a pas de
            stratégie à régler, c'est l'owner la stratégie. */}
        <PageOwner variant="auto" />
        {loading && <LoadingState lines={4} />}
        {error && <ErrorState title={error} testId="settings-error" />}
        {data && (
          <>
            <SettingsStrip settings={data} />

            <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
              {/* ── work column ────────────────────────────────────────── */}
              <div className="flex min-w-0 flex-col gap-4">
                <Panel
                  title="Paramètres"
                  aria-label="Paramètres de trading et de risque"
                  aside={<span>OTP · entre-cycles · jamais un ordre IG</span>}
                >
                  <div className="flex flex-col gap-4">
                    <ParamEditForm
                      onCreated={() => {
                        reload();
                        setVersionsKey((k) => k + 1);
                      }}
                      effective={data}
                    />
                    <div className="border-border/60 border-t pt-3">
                      <ParameterVersions key={versionsKey} />
                    </div>
                  </div>
                </Panel>

                <MarketCalendarPanel />
              </div>

              {/* ── reference rail ─────────────────────────────────────── */}
              <EffectiveRail settings={data} className="lg:sticky lg:top-20" />
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
